/**
 * The composer's rich-text editor: a document for a sandboxed frame.
 *
 * Writing HTML mail means holding HTML that was *received* -- the message being
 * replied to or forwarded -- in something editable. The reader keeps received
 * markup out of the app's document by rendering it in a sandboxed, opaque-origin
 * frame under a CSP that admits one script by hash (see `lib/html.ts`). The
 * editor is the same arrangement with the one script being an editor: a
 * `contenteditable` region, a few commands, and a serialiser that hands the
 * result back as strings. The app never parses that HTML; it stores it and
 * sends it.
 *
 * The script lives in `editor-frame.js`, loaded as text. Its hash is
 * {@link EDITOR_HASH}, pinned here, in `src-tauri/tauri.conf.json` and in
 * `deploy/security-headers.conf` -- a `srcdoc` document runs under its parent's
 * policy as well as its own -- and `dev/parity.mjs` recomputes it from the
 * file and checks all three. The Android build frames the same bytes through
 * the Rust core, which is why the script is a file and not a string in here.
 */

import script from "./editor-frame.js?raw";

/** The SHA-256 of `editor-frame.js`, base64, as CSP wants it. */
export const EDITOR_HASH = "sha256-kBD/vPhsYre5xyd5/G2lxElNyp477KlnkNWhy0XHrvc=";

/** What the app may ask the editor to do. */
export type EditorCommand =
  | "bold"
  | "italic"
  | "underline"
  | "strike"
  | "ul"
  | "ol"
  | "quote"
  | "link"
  | "unlink"
  | "clear";

/** The editor's report after every edit: markup to send, and its plain twin. */
export interface EditorContent {
  html: string;
  text: string;
  /** The writer's own words only -- everything above the quoted original. */
  written: string;
}

/** Which formatting is in force at the caret, for the toolbar to show. */
export interface EditorState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  ul: boolean;
  ol: boolean;
  quote: boolean;
  link: boolean;
}

export type EditorMessage =
  | { t: "ready" }
  | ({ t: "change" } & EditorContent)
  | ({ t: "state" } & EditorState)
  | { t: "blur" }
  | { t: "escape" };

/** The frame's policy: nothing in, nothing out, one script. */
function csp(allowRemoteImages: boolean): string {
  const img = allowRemoteImages ? "https: data:" : "data:";
  return [
    "default-src 'none'",
    `img-src ${img}`,
    "style-src 'unsafe-inline'",
    "font-src data:",
    `script-src '${EDITOR_HASH}'`,
    "frame-src 'none'",
    "form-action 'none'",
    "connect-src 'none'",
  ].join("; ");
}

/**
 * The editor's own styles. The writer's text in the app's reading face; the
 * quoted original in whatever it brought, under a left rule that travels with
 * the message (it is an inline style on the blockquote, set by `compose.ts`).
 */
function style(): string {
  return `
    :root { color-scheme: light; }
    :root[data-theme="dark"] { color-scheme: dark; }
    html, body { margin: 0; padding: 0; height: 100%; }
    body { background: #ffffff; color: #1a1d23; font: 14px/1.55 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; }
    :root[data-theme="dark"] body { background: #0e1014; color: #e6e8ec; }
    #hm-edit { min-height: calc(100% - 28px); padding: 12px 14px 16px; outline: none; overflow-wrap: anywhere; }
    #hm-edit:empty::before, .hm-written:empty::before { content: attr(data-placeholder); color: #8a909b; }
    #hm-edit img { max-width: 100%; height: auto; }
    #hm-edit a { color: #2f6fed; }
    :root[data-theme="dark"] #hm-edit a { color: #7aa7ff; }
    #hm-edit blockquote { margin: 0 0 0 0.8ex; border-left: 1px solid #ccc; padding-left: 1ex; }
    .hm-quote { margin-top: 12px; }
  `;
}

/**
 * The document to load into the editor frame. Content arrives afterwards by
 * message (`init`), never in the markup: the frame's HTML is the same for
 * every message, so there is nothing a message could do to it on the way in.
 */
export function editorDocument(dark: boolean, allowRemoteImages: boolean): string {
  return `<!doctype html><html data-theme="${dark ? "dark" : "light"}"><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${csp(allowRemoteImages)}">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="color-scheme" content="${dark ? "dark" : "light"}">
<style>${style()}</style></head><body><div id="hm-edit" contenteditable="true" spellcheck="true" data-placeholder="Write something…"></div><script>${script}</script></body></html>`;
}

/** The raw script, for the parity guard and the Rust twin's test to hash. */
export const EDITOR_SCRIPT = script;
